import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  Box,
  TextField,
  Grid,
  Card,
  CardContent,
  CardActionArea,
  InputAdornment,
  List,
  ListItem,
  ListItemText,
  Paper,
  Divider,
  Button,
} from '@mui/material';
import {
  Search as SearchIcon,
  PlayCircle,
  ShoppingCart,
  People,
  Inventory,
  Receipt,
  Assessment,
  Settings,
  Build,
  TrendingUp,
  School,
  Email,
} from '@mui/icons-material';
import { helpCategories, searchArticles } from '../data/helpContent';

const iconMap = {
  PlayCircle: PlayCircle,
  ShoppingCart: ShoppingCart,
  People: People,
  Inventory: Inventory,
  Receipt: Receipt,
  Assessment: Assessment,
  Settings: Settings,
  Build: Build,
};

const Help = () => {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);

  const handleSearch = (query) => {
    setSearchQuery(query);
    if (query.trim().length >= 2) {
      const results = searchArticles(query);
      setSearchResults(results);
    } else {
      setSearchResults([]);
    }
  };

  const handleArticleClick = (articleId) => {
    navigate(`/help/article/${articleId}`);
  };

  const handleCategoryClick = (categoryId) => {
    navigate(`/help/category/${categoryId}`);
  };

  const popularArticles = [
    { id: 'creating-sale', title: 'Creating a New Sale', category: 'Managing Sales' },
    { id: 'adding-inventory', title: 'Adding Inventory Items', category: 'Inventory' },
    { id: 'lead-pipeline', title: 'Understanding the Lead Pipeline', category: 'Lead Management' },
    { id: 'commission-settings', title: 'Configuring Commission Rates', category: 'Settings & Admin' },
    { id: 'creating-invoices', title: 'Creating Client Invoices', category: 'Invoices & Payments' },
  ];

  return (
    <Container maxWidth="lg">
      <Box sx={{ py: 4 }}>
        {/* Header */}
        <Box sx={{ textAlign: 'center', mb: 6 }}>
          <Typography variant="h3" gutterBottom sx={{ fontWeight: 600 }}>
            Help Center
          </Typography>
          <Typography variant="h6" color="text.secondary" gutterBottom>
            Find answers and learn how to use PaceEstate effectively
          </Typography>

          {/* Search Bar */}
          <Box sx={{ mt: 4, maxWidth: 600, mx: 'auto' }}>
            <TextField
              fullWidth
              placeholder="Search for help articles..."
              value={searchQuery}
              onChange={(e) => handleSearch(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  backgroundColor: 'background.paper',
                },
              }}
            />

            {/* Search Results */}
            {searchResults.length > 0 && (
              <Paper sx={{ mt: 1, maxHeight: 400, overflow: 'auto' }}>
                <List>
                  {searchResults.map((article, index) => (
                    <React.Fragment key={article.id}>
                      <ListItem
                        button
                        onClick={() => handleArticleClick(article.id)}
                      >
                        <ListItemText
                          primary={article.title}
                          secondary={article.categoryTitle}
                        />
                      </ListItem>
                      {index < searchResults.length - 1 && <Divider />}
                    </React.Fragment>
                  ))}
                </List>
              </Paper>
            )}

            {searchQuery.trim().length >= 2 && searchResults.length === 0 && (
              <Paper sx={{ mt: 1, p: 2 }}>
                <Typography color="text.secondary" align="center">
                  No articles found for "{searchQuery}"
                </Typography>
              </Paper>
            )}
          </Box>
        </Box>

        {/* Categories Grid */}
        <Box sx={{ mb: 6 }}>
          <Typography variant="h4" gutterBottom sx={{ mb: 3, fontWeight: 600 }}>
            Browse by Category
          </Typography>
          <Grid container spacing={3}>
            {helpCategories.map((category) => {
              const IconComponent = iconMap[category.icon];
              return (
                <Grid item xs={12} sm={6} md={4} key={category.id}>
                  <Card
                    sx={{
                      height: '100%',
                      transition: 'transform 0.2s, box-shadow 0.2s',
                      '&:hover': {
                        transform: 'translateY(-4px)',
                        boxShadow: 4,
                      },
                    }}
                  >
                    <CardActionArea
                      onClick={() => handleCategoryClick(category.id)}
                      sx={{ height: '100%', p: 2 }}
                    >
                      <CardContent>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                          {IconComponent && (
                            <IconComponent
                              sx={{
                                fontSize: 40,
                                color: 'primary.main',
                                mr: 2,
                              }}
                            />
                          )}
                          <Typography variant="h6" component="div">
                            {category.title}
                          </Typography>
                        </Box>
                        <Typography variant="body2" color="text.secondary">
                          {category.description}
                        </Typography>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ mt: 2, display: 'block' }}
                        >
                          {category.articles.length} articles
                        </Typography>
                      </CardContent>
                    </CardActionArea>
                  </Card>
                </Grid>
              );
            })}
          </Grid>
        </Box>

        {/* Popular Articles */}
        <Box sx={{ mb: 6 }}>
          <Typography variant="h4" gutterBottom sx={{ mb: 3, fontWeight: 600 }}>
            Popular Articles
          </Typography>
          <Grid container spacing={2}>
            {popularArticles.map((article) => (
              <Grid item xs={12} md={6} key={article.id}>
                <Card>
                  <CardActionArea onClick={() => handleArticleClick(article.id)}>
                    <CardContent>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <TrendingUp sx={{ mr: 2, color: 'primary.main' }} />
                        <Box>
                          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                            {article.title}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {article.category}
                          </Typography>
                        </Box>
                      </Box>
                    </CardContent>
                  </CardActionArea>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Box>

        {/* Contact Support Section */}
        <Paper sx={{ p: 4, textAlign: 'center', bgcolor: 'primary.main', color: 'white' }}>
          <School sx={{ fontSize: 60, mb: 2 }} />
          <Typography variant="h5" gutterBottom>
            Still Need Help?
          </Typography>
          <Typography variant="body1" sx={{ mb: 3, opacity: 0.9 }}>
            Can't find what you're looking for? Our support team is here to help.
          </Typography>
          <Button
            variant="contained"
            size="large"
            startIcon={<Email />}
            sx={{
              bgcolor: 'white',
              color: 'primary.main',
              '&:hover': {
                bgcolor: 'grey.100',
              },
            }}
            href="mailto:support@paceestate.com"
          >
            Contact Support
          </Button>
        </Paper>
      </Box>
    </Container>
  );
};

export default Help;
